import { type ReactNode, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Lightweight hover/focus tooltip (design system). Renders its `label` in a
 * portal on `document.body` with `position: fixed`, so it escapes any
 * `overflow:hidden` ancestor (e.g. a list card, or the collapsed sidebar rail)
 * and is never clipped. With `side="auto"` (default) it sits above the trigger,
 * flipping below near the top edge; `side="right"` places it to the right
 * (vertically centered) — for collapsed nav rails. `pointer-events: none`, so it
 * never steals the hover or a click on the trigger.
 */
type Coords = { left: number; top: number; placement: 'top' | 'bottom' | 'right' }

export default function Tooltip({
  label,
  children,
  className = '',
  side = 'auto',
  wrapperClassName = 'inline-flex',
}: {
  label: ReactNode
  children: ReactNode
  className?: string
  side?: 'auto' | 'right'
  wrapperClassName?: string
}) {
  const ref = useRef<HTMLSpanElement>(null)
  const [coords, setCoords] = useState<Coords | null>(null)

  function show() {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    if (side === 'right') {
      setCoords({ left: r.right, top: r.top + r.height / 2, placement: 'right' })
      return
    }
    const placement: Coords['placement'] = r.top < 160 ? 'bottom' : 'top'
    const left = Math.min(Math.max(r.left + r.width / 2, 88), window.innerWidth - 88)
    setCoords({ left, top: placement === 'top' ? r.top : r.bottom, placement })
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: passive trigger wrapper — hover/focus only reveals a non-essential tooltip, so no interactive role applies
    <span
      ref={ref}
      onMouseEnter={show}
      onMouseLeave={() => setCoords(null)}
      onFocus={show}
      onBlur={() => setCoords(null)}
      className={wrapperClassName}
    >
      {children}
      {coords
        ? createPortal(
            <div
              role="tooltip"
              style={{
                left: coords.left,
                top: coords.top,
                transform:
                  coords.placement === 'top'
                    ? 'translate(-50%, calc(-100% - 8px))'
                    : coords.placement === 'bottom'
                      ? 'translate(-50%, 8px)'
                      : 'translate(8px, -50%)',
              }}
              className={`pointer-events-none fixed z-[100] max-w-xs rounded-lg border border-border bg-surface px-2.5 py-2 text-ink shadow-lg ${className}`}
            >
              {label}
            </div>,
            document.body,
          )
        : null}
    </span>
  )
}
